import ActionButton from 'lib/src/components/button/ActionButton';
import ButtonRow from 'lib/src/components/button/ButtonRow';
import { useState } from 'react';
import { AssessmentScoreResponse, AssessmentSummary } from 'src/types/shared/Assessments';
import ScoreSummaryTable from '../../../../components/scoreSummaryTable/ScoreSummaryTable';
import { getColor } from 'lib/src/utils/generic';
import { useParams } from 'react-router';
import { useSelector } from 'react-redux';
import { getChallenge } from '@selectors/challenges';
import { RootState } from '@reducers/index';
import EditScoreSummaryCell from './EditScoreSummaryCell';
import useHasChallengeCrudPermissions from '../../../../utils/hooks/useHasChallengeCrudPermissions';
import useFetchChallengeGroup from '@pages/challengeGroups/hooks/useFetchChallengeGroup';

const ScoreSummaryTableComponent: React.FC<ScoreSummaryTableProps> = ({ data }) => {
    const [currentRow, setCurrentRow] = useState<number | null>(null);
    const handleSetCurrentRow = (row: number) => {
        if (!!currentRow) setCurrentRow(null);
        else setCurrentRow(row);
    };

    const getInnovativeScore = (assessmentScores: AssessmentScoreResponse[]) => {
        return assessmentScores.reduce((prev, curr) => {
            return prev + (!!curr.assessment.isInnovative ? 1 : 0);
        }, 0);
    };

    const { id: groupId, challengeid } = useParams<{ id: string; challengeid: string }>();

    const challenge = useSelector((state: RootState) => getChallenge(state, +challengeid));
    const { challengeGroup } = useFetchChallengeGroup(+groupId);

    const hasChallengeCrudPermissions = useHasChallengeCrudPermissions(challengeGroup, challenge);

    const columns = [
        {
            key: 1,
            heading: 'Submission',
            getValue: (row: AssessmentSummary) => `${row.submission.companyName}` || '-',
        },
        {
            key: 2,
            heading: 'Deployment timescale',
            getValue: (row: AssessmentSummary) => (
                <span className={getColor(row.averageScore.deploymentScale)}>
                    {`${row.averageScore.deploymentScale + ' pts'}` || '-'}
                </span>
            ),
        },
        {
            key: 3,
            heading: 'Fit with challenge',
            getValue: (row: AssessmentSummary) => (
                <span className={getColor(row.averageScore.fitWithChallenge)}>
                    {`${row.averageScore.fitWithChallenge + ' pts'}` || '-'}
                </span>
            ),
        },
        {
            key: 4,
            heading: 'Credibility of company',
            getValue: (row: AssessmentSummary) => (
                <span className={getColor(row.averageScore.credibilityCompany)}>
                    {`${row.averageScore.credibilityCompany + ' pts'}` || '-'}
                </span>
            ),
        },
        {
            key: 5,
            heading: 'Credibility of technology',
            getValue: (row: AssessmentSummary) => (
                <span className={getColor(row.averageScore.credibilityTechnology)}>
                    {`${row.averageScore.credibilityTechnology + ' pts'}` || '-'}
                </span>
            ),
        },
        {
            key: 6,
            heading: 'Full or partial solution',
            getValue: (row: AssessmentSummary) => (
                <span className={getColor(row.averageScore.fullOrPartialSolution)}>
                    {`${row.averageScore.fullOrPartialSolution + ' pts'}` || '-'}
                </span>
            ),
        },
        {
            key: 7,
            heading: 'Is it innovative?',
            getValue: (row: AssessmentSummary) => (
                <span
                    className={getColor(getInnovativeScore(row.assessmentScores))}
                >{`${getInnovativeScore(row.assessmentScores)} of ${
                    row.assessmentScores.length
                }`}</span>
            ),
        },
        {
            key: 8,
            heading: 'Score',
            getValue: (row: AssessmentSummary) => (
                <span className={getColor(row.averageScore.total / 5)}>
                    {`${row.averageScore.total + ' pts'}` || '-'}
                </span>
            ),
        },
        {
            key: 9,
            heading: 'Summary',
            getValue: (row: AssessmentSummary) =>
                !!hasChallengeCrudPermissions ? (
                    <EditScoreSummaryCell row={row} />
                ) : (
                    `${
                        !!row.submission.overallScoreSummary
                            ? row.submission.overallScoreSummary
                            : '-'
                    }`
                ),
        },
        {
            key: 10,
            heading: '',
            getValue: (row: AssessmentSummary) => (
                <ButtonRow>
                    <ActionButton
                        // disabled={!row.assessmentScores?.length}
                        color="blue"
                        icon={currentRow === row.submission.id ? 'chevron-up' : 'chevron-down'}
                        type="button"
                        onClick={() => handleSetCurrentRow(row.submission.id)}
                    ></ActionButton>
                </ButtonRow>
            ),
        },
    ];

    console.log(data.map(d => ({ ...d, id: d.submission.id })));

    return (
        <div className="summary-table">
            <ScoreSummaryTable
                currentRow={currentRow}
                columns={columns}
                rows={data.map(d => ({ ...d, id: d.submission.id }))}
                isLoading={false}
            />
        </div>
    );
};

interface ScoreSummaryTableProps {
    data: AssessmentSummary[];
    isFetching: boolean;
}

export default ScoreSummaryTableComponent;
